import React from 'react';
import { Box, Chip } from '@mui/material';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import { useTranslation } from 'react-i18next';
import { useCasePresence } from '../../../Contexts/CasePresenceContext';

/**
 * Warns that other users have the open client's case open too, so two people do not unknowingly
 * edit the same books. Rendered once in the layout so it covers every client screen, and renders
 * nothing when no client is open or nobody else is in.
 *
 * Floats at the top of the screen because the risk lasts as long as the other person is working,
 * not just until the page is scrolled. The z-index sits at MUI's app-bar level: above page content,
 * below drawers and dialogs so it never covers a modal. The names sit on the first line and the
 * consequence on the second, so the warning reads without a hover.
 */
const ConcurrentViewersBanner = () => {
    const { t } = useTranslation();
    const { otherViewers } = useCasePresence();

    if (!otherViewers.length) return null;

    const names = otherViewers.map(viewer => viewer.name).join(', ');

    return (
        <Box
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1100,
                display: 'flex',
                justifyContent: 'center',
                pt: 0.75,
                // The strip spans the full width, so let clicks through to the page underneath.
                pointerEvents: 'none',
            }}
        >
            <Chip
                role="status"
                icon={<GroupsRoundedIcon />}
                label={
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.3, py: 0.15 }}>
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                            }}
                        >
                            {t('ConcurrentViewersWarning', { names })}
                        </Box>
                        <Box component="span" sx={{ fontWeight: 500, fontSize: '11px', opacity: 0.85 }}>
                            {t('ConcurrentViewersWarningTooltip')}
                        </Box>
                    </Box>
                }
                sx={{
                    pointerEvents: 'auto',
                    height: 'auto',
                    py: '5px',
                    maxWidth: '92%',
                    borderRadius: '16px',
                    backgroundColor: '#fef3f2',
                    border: '1px solid #fecdca',
                    boxShadow: '0 2px 10px rgba(180, 35, 24, 0.14)',
                    '& .MuiChip-label': {
                        fontSize: '12px',
                        color: '#b42318',
                        display: 'block',
                        overflow: 'hidden',
                    },
                    '& .MuiChip-icon': {
                        color: '#d92d20',
                        fontSize: '18px',
                        marginInlineStart: '8px',
                        alignSelf: 'center',
                    },
                }}
            />
        </Box>
    );
};

export default ConcurrentViewersBanner;
